import { motion } from 'framer-motion';
import { theme } from '@/lib/theme';

export type CalendarViewMode = 'day' | 'week' | 'month';

interface Props {
  viewMode: CalendarViewMode;
  onViewChange: (mode: CalendarViewMode) => void;
  currentDate: Date;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
}

const VIEW_OPTIONS: { value: CalendarViewMode; label: string }[] = [
  { value: 'day', label: 'Día' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
];

function formatTitle(date: Date, mode: CalendarViewMode): string {
  if (mode === 'day') {
    return date.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }
  if (mode === 'week') {
    const end = new Date(date);
    end.setDate(date.getDate() + 6);
    const startStr = date.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
    const endStr = end.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${startStr} – ${endStr}`;
  }
  return date.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
}

export default function CalendarToolbar({ viewMode, onViewChange, currentDate, onPrev, onNext, onToday }: Props) {
  return (
    <div
      className="flex items-center justify-between px-5 py-3 border-b flex-wrap gap-3"
      style={{ borderColor: theme.colors.border }}
    >
      {/* Navigation */}
      <div className="flex items-center gap-2">
        <button
          onClick={onPrev}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/8 text-white/50 hover:text-white transition-all cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-base" />
        </button>

        <button
          onClick={onToday}
          className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
          style={{
            backgroundColor: theme.colors.primaryMuted,
            color: theme.colors.primary,
            border: `1px solid ${theme.colors.primaryBorder}`,
          }}
        >
          Hoy
        </button>

        <button
          onClick={onNext}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/8 text-white/50 hover:text-white transition-all cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-base" />
        </button>

        <h3 className="text-white text-sm font-semibold ml-1 capitalize">
          {formatTitle(currentDate, viewMode)}
        </h3>
      </div>

      {/* View mode switcher */}
      <div
        className="flex items-center p-1 rounded-lg"
        style={{ backgroundColor: theme.colors.bgBase }}
      >
        {VIEW_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onViewChange(opt.value)}
            className="relative px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
            style={{
              color: viewMode === opt.value ? theme.colors.primary : 'rgba(255,255,255,0.4)',
            }}
          >
            {viewMode === opt.value && (
              <motion.div
                layoutId="calendar-view-indicator"
                className="absolute inset-0 rounded-md"
                style={{ backgroundColor: theme.colors.primaryMuted, border: `1px solid ${theme.colors.primaryBorder}` }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative z-10">{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
